/**
 * Reference Number & Short Customer Reference Utilities for Uniform Loop
 * 
 * Standard Short Customer References:
 * - UL48291 / UL-48291 / UL92153
 * 
 * Internally supports legacy prefixes:
 * - UL-RES-YYYYMMDD-XXXXXX
 * - UL-MSG-YYYYMMDD-XXXXXX
 */

/**
 * Generates a short, user-friendly 5-digit customer reference.
 * Example output: "UL48291"
 */
export function generateShortReservationCode(): string {
  // 5-digit random number between 10000 and 99999
  const random5Digits = Math.floor(10000 + Math.random() * 90000).toString();
  return `UL${random5Digits}`;
}

/**
 * Formats or extracts a clean short customer reference from any reservation object or string.
 * Example inputs -> Outputs:
 * - "UL48291" -> "UL48291"
 * - "UL-48291" -> "UL48291"
 * - "UL-RES-20260811-849201" -> "UL84920"
 * - Raw Firestore ID "aB3k9P1..." -> "UL39482" (consistent hash)
 */
export function getCustomerReference(
  target: string | { reservationCode?: string; reservationNumber?: string; customerReference?: string; id?: string } | null | undefined
): string {
  if (!target) return 'UL00000';

  if (typeof target === 'object') {
    if (target.customerReference) return formatAsShortRef(target.customerReference);
    if (target.reservationCode) return formatAsShortRef(target.reservationCode);
    if (target.reservationNumber) return formatAsShortRef(target.reservationNumber);
    if (target.id) return formatAsShortRef(target.id);
    return 'UL00000';
  }

  return formatAsShortRef(target);
}

/**
 * Formats any raw string into a clean customer-facing reference (e.g. UL48291).
 */
function formatAsShortRef(raw: string): string {
  if (!raw) return 'UL00000';
  const str = String(raw).trim();

  // If already matches UL followed by 5 digits (with or without hyphen)
  const ul5Match = str.match(/^UL-?(\d{5})$/i);
  if (ul5Match) {
    return `UL${ul5Match[1]}`;
  }

  // If matches legacy UL-RES-YYYYMMDD-XXXXXX pattern, take the last 5 digits
  const legacyMatch = str.match(/^UL-RES-\d{8}-(\d{5,6})$/i);
  if (legacyMatch) {
    const digits = legacyMatch[1].slice(-5);
    return `UL${digits}`;
  }

  // If starts with UL-RES or UL-MSG with some numbers
  const genericDigits = str.replace(/\D/g, '');
  if (genericDigits.length >= 5) {
    return `UL${genericDigits.slice(-5)}`;
  }

  // If it's a short alphanumeric string without enough digits, generate a stable 5-digit number from string hash
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  const positiveHash = Math.abs(hash) % 90000 + 10000;
  return `UL${positiveHash}`;
}

/**
 * Normalizes user input for reservation reference lookup.
 * Handles inputs like:
 * - 48291
 * - UL48291
 * - UL-48291
 * - ul48291
 * - UL-RES-20260811-048291
 * 
 * Returns canonical format e.g. "UL48291" and clean digits "48291".
 */
export function normalizeReservationReference(input: string): { canonical: string; digits: string; rawUpper: string } {
  if (!input) return { canonical: '', digits: '', rawUpper: '' };

  const rawUpper = input.trim().toUpperCase();
  // Strip non-alphanumeric chars
  const alphanumeric = rawUpper.replace(/[^A-Z0-9]/g, '');
  
  // Extract all digits
  const digits = rawUpper.replace(/\D/g, '');

  let canonical = rawUpper;
  if (/^\d{5}$/.test(alphanumeric)) {
    // Just 5 digits (e.g. "48291") -> "UL48291"
    canonical = `UL${alphanumeric}`;
  } else if (/^UL\d{5}$/.test(alphanumeric)) {
    // "UL48291" -> "UL48291"
    canonical = alphanumeric;
  } else if (digits.length >= 5) {
    canonical = `UL${digits.slice(-5)}`;
  }

  return { canonical, digits, rawUpper };
}

/**
 * Matches a reservation against a user-supplied reference code.
 */
export function matchesReservationReference(
  reservation: { reservationCode?: string; reservationNumber?: string; customerReference?: string; id?: string },
  userInput: string
): boolean {
  if (!reservation || !userInput) return false;

  const { canonical, digits, rawUpper } = normalizeReservationReference(userInput);
  if (!canonical && !rawUpper) return false;

  // Get all possible string representations of the reservation
  const resShortCode = getCustomerReference(reservation).toUpperCase();
  const resCode = (reservation.reservationCode || '').trim().toUpperCase();
  const resNumber = (reservation.reservationNumber || '').trim().toUpperCase();
  const resId = (reservation.id || '').trim().toUpperCase();
  const resCustomerRef = (reservation.customerReference || '').trim().toUpperCase();

  const candidates = [resShortCode, resCode, resNumber, resId, resCustomerRef].filter(Boolean);

  for (const cand of candidates) {
    const candNorm = cand.replace(/[^A-Z0-9]/g, '');
    const candDigits = cand.replace(/\D/g, '');

    // 1. Direct exact or normalized canonical match (e.g. UL48291 === UL48291)
    if (cand === rawUpper || candNorm === canonical || candNorm === rawUpper.replace(/[^A-Z0-9]/g, '')) {
      return true;
    }

    // 2. If user searched by 5-digit number e.g. "48291", check if candidate ends with or contains those 5 digits
    if (digits.length >= 4 && (candDigits.endsWith(digits) || candNorm.includes(digits))) {
      return true;
    }

    // 3. Match against short code representation
    if (resShortCode === canonical || resShortCode.replace('UL', '') === digits) {
      return true;
    }
  }

  return false;
}

/**
 * Legacy generator for backward-compatibility if needed.
 */
export function generateReferenceNumber(prefix: 'UL-RES' | 'UL-MSG'): string {
  if (prefix === 'UL-RES') {
    return generateShortReservationCode();
  }
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateStr = `${year}${month}${day}`;
  const randomSuffix = Math.floor(100000 + Math.random() * 900000).toString();
  return `${prefix}-${dateStr}-${randomSuffix}`;
}

